import type { IImage } from "@components/types/media";

import { env } from "@/env";
import { getAspectRatio } from "@ui/components/core/AspectRatio";
import { breakpoints } from "@ui/tokens/grid";
import { isEntity } from "@utilities/payload";
import type { Media } from "@mw/shared/payload-types";
import type { StaticImport } from "next/dist/shared/lib/get-img-props";
import { getImageProps as getNextImageProps } from "next/image";

/** Extracts a plain string URL from `ImageProps.src`, which may be a string or a static import. */
export function resolveImageSrc(src: string | StaticImport): string {
  if (typeof src === "string") return src;
  if ("default" in src) return src.default.src; // StaticRequire (require("...png"))
  return src.src; // StaticImageData (import cover from "...png")
}

export const getImageAspectRatio = (image?: IImage | null) => {
  if (!image) return null;
  if (typeof image.resource === "string") return null;
  if (typeof image.resource === "number") return null;
  if (!image.resource?.width || !image.resource?.height) return null;
  return getAspectRatio(Number(image.resource.width), Number(image.resource.height));
};

export const isValidImage = (image?: IImage | null): image is IImage => {
  return (
    !!image?.src?.length ||
    (typeof image?.resource !== "string" && typeof image?.resource !== "number" && !!image?.resource?.url?.length)
  );
};
/**
 * Define sizes constants based on the breakpoints
 * To use with the `sizes` prop of the `Image` component
 *
 * @deprecated Deprecated in favor of explicit size, see helper from lib/sizes.ts
 */
export const ImageSizes = {
  FULL_HALF_ON_LG: `(min-width: ${breakpoints.lg}) 50vw, 100vw`,
  FULL_3_4_ON_LG: `(min-width: ${breakpoints.lg}) 75vw, 100vw`,
  FULL_1_3_ON_LG: `(min-width: ${breakpoints.lg}) 33vw, 90vw`,
  FULL_1_4_ON_LG_HALF_ON_MD: `(min-width: ${breakpoints.lg}) 25vw, (min-width: ${breakpoints.md}) 50vw, 100vw`,
  INDUSTRY_CARDS: `(min-width: ${breakpoints.lg}) 33vw, (min-width: ${breakpoints.md} and max-width: ${breakpoints.lg}) 100vw, 160px`,
  CONTENT_CARDS: `(min-width: ${breakpoints.lg}) 33vw, (min-width: ${breakpoints.md}) and (max-width: ${breakpoints.lg}) 50vw, 100vw`,
  FULL: `100vw`,
} as const;

export async function dynamicBlurDataUrl(url: string) {
  const baseUrl = env.NEXT_PUBLIC_SERVER_URL;

  const base64str = await fetch(`${baseUrl}/_next/image?url=${url}&w=16&q=75`).then(async res =>
    Buffer.from(await res.arrayBuffer()).toString("base64"),
  );

  const blurSvg = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 5'>
      <filter id='b' color-interpolation-filters='sRGB'>
        <feGaussianBlur stdDeviation='1' />
      </filter>

      <image preserveAspectRatio='none' filter='url(#b)' x='0' y='0' height='100%' width='100%'
      href='data:image/avif;base64,${base64str}' />
    </svg>
  `;

  const toBase64 = (str: string) =>
    typeof window === "undefined" ? Buffer.from(str).toString("base64") : window.btoa(str);

  return `data:image/svg+xml;base64,${toBase64(blurSvg)}`;
}

export function sizeWithWidth(image: Media | string | null | undefined, width: number) {
  if (!isEntity(image)) {
    return null;
  }

  const imageWidth = image.width ?? 0;
  const imageHeight = image.height ?? 0;

  if (imageWidth === 0 || imageHeight === 0) {
    return null;
  }

  const height = imageHeight * (width / imageWidth);

  return { width, height };
}

export function sizeWithHeight(image: Media | string | null | undefined, height: number) {
  if (!isEntity(image)) {
    return null;
  }

  const imageWidth = image.width ?? 0;
  const imageHeight = image.height ?? 0;

  if (imageWidth === 0 || imageHeight === 0) {
    return null;
  }

  const width = imageWidth * (height / imageHeight);

  return { width, height };
}

// Returns a CSS background image string for a given media asset, with a specified width and/or height.
export function getBackgroundImage(
  image: Media | string | null | undefined,
  opts: { width?: number; height?: number } = {},
) {
  if (!isEntity(image) || !image.url) {
    return null;
  }

  let width = 0;
  let height = 0;

  if (typeof opts.width === "undefined" && typeof opts.height === "undefined") {
    width = image.width ?? 0;
    height = image.height ?? 0;
  } else if (typeof opts.width === "number" && typeof opts.height === "undefined") {
    width = opts.width;
    height = sizeWithWidth(image, opts.width)?.height ?? 0;
  } else if (typeof opts.width === "undefined" && typeof opts.height === "number") {
    width = sizeWithHeight(image, opts.height)?.width ?? 0;
    height = opts.height;
  } else {
    width = opts.width ?? 0;
    height = opts.height ?? 0;
  }

  if (width === 0 || height === 0) {
    return null;
  }

  const {
    props: { src, srcSet },
  } = getNextImageProps({
    alt: "",
    width,
    height,
    src: image.url,
  });

  if (srcSet) {
    // https://nextjs.org/docs/app/api-reference/components/image#background-css
    const imageSet = srcSet
      .split(", ")
      .map(str => {
        const [url, dpi] = str.split(" ");
        return `url("${url}") ${dpi}`;
      })
      .join(", ");
    return `image-set(${imageSet})`;
  }

  return `url(${src})`;
}
